import {
  cardTypeSchema,
  deckFormatSchema,
  deckLinkSchema,
  deckPlanResponseSchema,
  deckZoneSchema,
  domainSchema,
  formatConfigResponseSchema,
} from "@openrift/shared/response-schemas";
import { idParamSchema, withParams, xidWatermarkSchema } from "@openrift/shared/schemas";
import { z } from "zod";

import { authedRoute } from "./_base.js";
import { nameField, shareStateResponseSchema } from "./fields.js";

export const MAX_DECK_LINKS = 5;

const deckFieldRules = {
  name: nameField(200),
  format: z.string().min(1),
};

const deckCardFieldRules = {
  zone: z.string().min(1),
  quantity: z.number().int().positive(),
};

export const decksQuerySchema = z.object({
  includeArchived: z.enum(["true", "false"]).optional(),
});

/** Loose on purpose: the column is jsonb and validation lives in the route handler. */
const formatConfigSchema = z.record(z.string(), z.unknown()).nullable();

export const createDeckSchema = z.object({
  id: z.uuid().optional(),
  name: deckFieldRules.name,
  description: z.string().max(8000).nullish(),
  format: deckFieldRules.format,
  formatConfig: formatConfigSchema.optional(),
  isPublic: z.boolean().optional(),
  links: z.array(deckLinkSchema).max(MAX_DECK_LINKS).optional(),
});

/** An AND of optional conditions over structured card data; list fields matched any-of. */
export const deckOddsGroupSchema = z.object({
  key: z.string().min(1).max(80),
  label: z.string().min(1).max(80),
  types: z.array(z.string().min(1).max(40)).max(5).optional(),
  keywords: z.array(z.string().min(1).max(40)).max(10).optional(),
  tags: z.array(z.string().min(1).max(60)).max(5).optional(),
  energyMin: z.number().int().min(0).max(99).optional(),
  energyMax: z.number().int().min(0).max(99).optional(),
  mightMin: z.number().int().min(0).max(99).optional(),
  powerMin: z.number().int().min(0).max(99).optional(),
});

/** `selection: null` means the suggested defaults. */
export const deckOddsConfigSchema = z.object({
  customGroups: z.array(deckOddsGroupSchema).max(20),
  selection: z.array(z.string().min(1).max(80)).max(60).nullable(),
});

export type DeckOddsGroup = z.infer<typeof deckOddsGroupSchema>;
export type DeckOddsConfig = z.infer<typeof deckOddsConfigSchema>;

// isPublic is intentionally absent: a deck's public state is controlled only by
// the /decks/{id}/share sub-resource, not by PATCH.
export const updateDeckSchema = z.object({
  name: deckFieldRules.name.optional(),
  description: z.string().max(8000).nullish(),
  format: deckFieldRules.format.optional(),
  formatConfig: formatConfigSchema.optional(),
  oddsConfig: deckOddsConfigSchema.nullish(),
  coverCardId: z.uuid().nullish(),
  coverFaceId: z.uuid().nullish(),
  coverPosition: z.number().int().min(0).max(100).nullish(),
  links: z.array(deckLinkSchema).max(MAX_DECK_LINKS).optional(),
  collectionId: z.uuid().nullish(),
  isDraft: z.boolean().optional(),
});

export const updateDeckCardsSchema = z.object({
  cards: z
    .array(
      z.object({
        cardId: z.uuid(),
        zone: deckCardFieldRules.zone,
        quantity: deckCardFieldRules.quantity,
        preferredPrintingId: z.uuid().nullish(),
      }),
    )
    .max(500),
});

export const deckMatchupSwapDirectionSchema = z.enum(["in", "out"]);

const deckMatchupSwapSchema = z.object({
  cardId: z.uuid(),
  direction: deckMatchupSwapDirectionSchema,
  quantity: z.number().int().positive().max(99),
});

const deckMatchupPlanSchema = z
  .object({
    opponentCardId: z.uuid().nullable().default(null),
    opponentLabel: z.string().max(120).default(""),
    notes: z.string().max(4000).default(""),
    swaps: z.array(deckMatchupSwapSchema).max(40),
  })
  .refine((matchup) => matchup.opponentCardId !== null || matchup.opponentLabel.trim() !== "", {
    message: "A matchup needs an opponent: link a card or enter a name",
    path: ["opponentLabel"],
  });

/** Saved as a whole plan; not merged field by field. */
export const updateDeckPlanSchema = z.object({
  generalStrategy: z.string().max(8000).default(""),
  mulliganSplit: z.boolean().default(false),
  mulliganGeneral: z.string().max(4000).default(""),
  mulliganFirst: z.string().max(4000).default(""),
  mulliganSecond: z.string().max(4000).default(""),
  battlefieldGame1CardId: z.uuid().nullable().default(null),
  battlefieldFirstCardId: z.uuid().nullable().default(null),
  battlefieldSecondCardId: z.uuid().nullable().default(null),
  battlefieldCustom: z.boolean().default(false),
  battlefieldNote: z.string().max(4000).default(""),
  matchups: z.array(deckMatchupPlanSchema).max(40),
});

export const deckExportQuerySchema = z.object({
  format: z.enum(["piltover", "text", "tts"]).default("piltover"),
});

export const deckResponseSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  format: deckFormatSchema,
  formatConfig: formatConfigResponseSchema,
  isPublic: z.boolean(),
  shareToken: z.string().nullable(),
  isPinned: z.boolean(),
  archivedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  oddsConfig: deckOddsConfigSchema.nullable(),
  coverCardId: z.string().nullable(),
  coverFaceId: z.string().nullable(),
  coverPosition: z.number().int().nullable(),
  links: z.array(deckLinkSchema),
  collectionId: z.string().nullable(),
  familyId: z.string().nullable(),
  predecessorDeckId: z.string().nullable(),
  isPrimary: z.boolean(),
  isDraft: z.boolean(),
});

export const deckShareResponseSchema = shareStateResponseSchema;

export const deckCloneResponseSchema = z.object({
  deckId: z.string(),
});

export const deckSummaryResponseSchema = z.object({
  id: z.string(),
  name: z.string(),
  descriptionSnippet: z.string().nullable(),
  description: z.string().nullable(),
  links: z.array(deckLinkSchema),
  oddsConfig: deckOddsConfigSchema.nullable(),
  isPublic: z.boolean(),
  shareToken: z.string().nullable(),
  format: deckFormatSchema,
  formatConfig: formatConfigResponseSchema,
  isPinned: z.boolean(),
  archivedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  coverCardId: z.string().nullable(),
  coverFaceId: z.string().nullable(),
  coverPosition: z.number().int().nullable(),
  collectionId: z.string().nullable(),
  familyId: z.string().nullable(),
  predecessorDeckId: z.string().nullable(),
  isPrimary: z.boolean(),
  isDraft: z.boolean(),
});

export const deckListItemResponseSchema = z.object({
  deck: deckSummaryResponseSchema,
  legendCardId: z.string().nullable(),
  championCardId: z.string().nullable(),
  totalCards: z.number(),
  typeCounts: z.array(z.object({ cardType: cardTypeSchema, count: z.number() })),
  domainDistribution: z.array(z.object({ domain: domainSchema, count: z.number() })),
  isValid: z.boolean(),
  requiredProgress: z.number().int(),
  requiredTotal: z.number().int(),
  totalValueCents: z.number().int().nullable(),
  missingCount: z.number().int().nullable(),
  folderIds: z.array(z.string()),
});

export const deckListResponseSchema = z.object({ items: z.array(deckListItemResponseSchema) });

export const deckCardResponseSchema = z.object({
  cardId: z.string(),
  zone: deckZoneSchema,
  quantity: z.number(),
  preferredPrintingId: z.string().nullable(),
});

export const deckCardWithDeckResponseSchema = deckCardResponseSchema.extend({
  deckId: z.string(),
});

export const deckCardsQuerySchema = z.object({
  since: xidWatermarkSchema.optional(),
  cursor: z
    .string()
    .regex(/^\d{1,20}_[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/u)
    .optional(),
  limit: z.coerce.number().int().min(1).max(10_000).optional(),
});

export const deckCardsListResponseSchema = z.object({
  items: z.array(deckCardWithDeckResponseSchema),
  touchedDeckIds: z.array(z.uuid()).optional(),
  nextCursor: z.string().nullish(),
  syncedXid: z.string().optional(),
});

export const deckDetailResponseSchema = z.object({
  deck: deckResponseSchema,
  cards: z.array(deckCardResponseSchema),
});

export const deckPlanDetailResponseSchema = z.object({ plan: deckPlanResponseSchema });

export const deckCardsResponseSchema = z.object({ cards: z.array(deckCardResponseSchema) });

export const deckExportResponseSchema = z.object({
  code: z.string(),
  warnings: z.array(z.string()),
});

const TAG = "Decks";

export const createDeckVariantSchema = z.object({
  name: deckFieldRules.name.optional(),
});

/** `markAsPreviousVersion` is ignored when this deck already has a predecessor. */
export const linkDeckVariantSchema = z.object({
  otherDeckId: z.uuid(),
  markAsPreviousVersion: z.boolean().optional(),
});

export const setDeckPredecessorSchema = z.object({
  predecessorDeckId: z.uuid().nullable(),
});

const shareTokenParamSchema = z.object({ token: z.string().min(1) });

/** The archive copy composes its own name and provenance description; both fall back to the source deck's. */
export const cloneSharedDeckSchema = shareTokenParamSchema.extend({
  name: deckFieldRules.name.optional(),
  description: z.string().max(8000).optional(),
});
const pinDeckBodySchema = z.object({ isPinned: z.boolean() });
const archiveDeckBodySchema = z.object({ archived: z.boolean() });

export const decksContract = {
  list: authedRoute
    .route({ method: "GET", path: "/api/v1/decks", tags: [TAG] })
    .input(decksQuerySchema)
    .output(deckListResponseSchema),
  allCards: authedRoute
    .route({ method: "GET", path: "/api/v1/deck-cards", tags: [TAG] })
    .input(deckCardsQuerySchema)
    .output(deckCardsListResponseSchema),
  create: authedRoute
    .route({ method: "POST", path: "/api/v1/decks", tags: [TAG], successStatus: 201 })
    .input(createDeckSchema)
    .errors({
      BAD_REQUEST: { message: "Unknown format or invalid format config" },
      CONFLICT: { message: "Deck id already belongs to someone else" },
    })
    .output(deckResponseSchema),
  get: authedRoute
    .route({ method: "GET", path: "/api/v1/decks/{id}", tags: [TAG] })
    .input(idParamSchema)
    .errors({ NOT_FOUND: { message: "Deck not found" } })
    .output(deckDetailResponseSchema),
  update: authedRoute
    .route({ method: "PATCH", path: "/api/v1/decks/{id}", tags: [TAG] })
    .input(withParams(idParamSchema, updateDeckSchema))
    .errors({
      NOT_FOUND: { message: "Deck not found" },
      BAD_REQUEST: { message: "Unknown format or invalid format config" },
    })
    .output(deckResponseSchema),
  remove: authedRoute
    .route({ method: "DELETE", path: "/api/v1/decks/{id}", tags: [TAG], successStatus: 204 })
    .errors({ NOT_FOUND: { message: "Deck not found" } })
    .input(idParamSchema),
  replaceCards: authedRoute
    .route({ method: "PUT", path: "/api/v1/decks/{id}/cards", tags: [TAG] })
    .input(withParams(idParamSchema, updateDeckCardsSchema))
    .errors({ NOT_FOUND: { message: "Deck not found" } })
    .output(deckCardsResponseSchema),
  getPlan: authedRoute
    .route({ method: "GET", path: "/api/v1/decks/{id}/plan", tags: [TAG] })
    .input(idParamSchema)
    .errors({ NOT_FOUND: { message: "Deck not found" } })
    .output(deckPlanDetailResponseSchema),
  replacePlan: authedRoute
    .route({ method: "PUT", path: "/api/v1/decks/{id}/plan", tags: [TAG] })
    .input(withParams(idParamSchema, updateDeckPlanSchema))
    .errors({
      NOT_FOUND: { message: "Deck not found" },
      BAD_REQUEST: { message: "Invalid plan content" },
    })
    .output(deckPlanDetailResponseSchema),
  clone: authedRoute
    .route({ method: "POST", path: "/api/v1/decks/{id}/clone", tags: [TAG], successStatus: 201 })
    .input(idParamSchema)
    .errors({ NOT_FOUND: { message: "Deck not found" } })
    .output(deckResponseSchema),
  createVariant: authedRoute
    .route({
      method: "POST",
      path: "/api/v1/decks/{id}/variants",
      tags: [TAG],
      successStatus: 201,
    })
    .input(withParams(idParamSchema, createDeckVariantSchema))
    .errors({ NOT_FOUND: { message: "Deck not found" } })
    .output(deckResponseSchema),
  linkVariant: authedRoute
    .route({ method: "POST", path: "/api/v1/decks/{id}/link", tags: [TAG] })
    .input(withParams(idParamSchema, linkDeckVariantSchema))
    .errors({
      NOT_FOUND: { message: "Deck not found" },
      BAD_REQUEST: { message: "Decks cannot be linked" },
    })
    .output(deckResponseSchema),
  unlinkVariant: authedRoute
    .route({ method: "POST", path: "/api/v1/decks/{id}/unlink", tags: [TAG] })
    .input(idParamSchema)
    .errors({
      NOT_FOUND: { message: "Deck not found" },
      BAD_REQUEST: { message: "Deck has no variants" },
    })
    .output(deckResponseSchema),
  setPredecessor: authedRoute
    .route({ method: "PUT", path: "/api/v1/decks/{id}/predecessor", tags: [TAG] })
    .input(withParams(idParamSchema, setDeckPredecessorSchema))
    .errors({
      NOT_FOUND: { message: "Deck not found" },
      BAD_REQUEST: { message: "Decks cannot be linked" },
    })
    .output(deckResponseSchema),
  promotePrimary: authedRoute
    .route({ method: "POST", path: "/api/v1/decks/{id}/promote", tags: [TAG] })
    .input(idParamSchema)
    .errors({
      NOT_FOUND: { message: "Deck not found" },
      BAD_REQUEST: { message: "Deck has no variants" },
    })
    .output(deckResponseSchema),
  export: authedRoute
    .route({ method: "GET", path: "/api/v1/decks/{id}/export", tags: [TAG] })
    .input(withParams(idParamSchema, deckExportQuerySchema))
    .errors({ NOT_FOUND: { message: "Deck not found" } })
    .output(deckExportResponseSchema),
  setPinned: authedRoute
    .route({ method: "PATCH", path: "/api/v1/decks/{id}/pin", tags: [TAG] })
    .input(withParams(idParamSchema, pinDeckBodySchema))
    .errors({ NOT_FOUND: { message: "Deck not found" } })
    .output(deckResponseSchema),
  setArchived: authedRoute
    .route({ method: "PATCH", path: "/api/v1/decks/{id}/archive", tags: [TAG] })
    .input(withParams(idParamSchema, archiveDeckBodySchema))
    .errors({ NOT_FOUND: { message: "Deck not found" } })
    .output(deckResponseSchema),
  getShare: authedRoute
    .route({ method: "GET", path: "/api/v1/decks/{id}/share", tags: [TAG] })
    .input(idParamSchema)
    .errors({ NOT_FOUND: { message: "Deck not found" } })
    .output(deckShareResponseSchema),
  share: authedRoute
    .route({ method: "POST", path: "/api/v1/decks/{id}/share", tags: [TAG] })
    .input(idParamSchema)
    .errors({ NOT_FOUND: { message: "Deck not found" } })
    .output(deckShareResponseSchema),
  unshare: authedRoute
    .route({ method: "DELETE", path: "/api/v1/decks/{id}/share", tags: [TAG], successStatus: 204 })
    .errors({ NOT_FOUND: { message: "Deck not found" } })
    .input(idParamSchema),
  cloneShared: authedRoute
    .route({
      method: "POST",
      path: "/api/v1/decks/share/{token}/clone",
      tags: [TAG],
      successStatus: 201,
    })
    .input(cloneSharedDeckSchema)
    .errors({ NOT_FOUND: { message: "Shared deck not found" } })
    .output(deckCloneResponseSchema),
};

export type DecksContract = typeof decksContract;
